import { Box, OutlinedInput, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import { useSnackbar } from 'notistack'
import { useState } from 'react'

import { FormLabel, MButton } from 'components'
import { color } from 'config/color'
import { useAuth } from 'context'
import { useNavigate } from 'react-router-dom'
import { isAuthorized, useMobileBreakpoints } from 'utils'

export const AccountManager: React.FC = () => {
  const { getUser, handleLogOut } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [accountId, setAccountId] = useState('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const isMobile = useMobileBreakpoints()
  // const isWEBClient = process.env.REACT_APP_CLIENT_NAME === 'web'

  const handleSubmit = async (): Promise<void> => {
    try {
      setIsLoading(true)
      const response = await getUser(accountId, email)
      if (response.length === 0) {
        enqueueSnackbar('No user found', {
          variant: 'error',
          autoHideDuration: 3000
        })
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      } else if (response?.errorMessage) {
        enqueueSnackbar(response?.errorMessage, {
          variant: 'error',
          autoHideDuration: 3000
        })
      } else {
        navigate(`/account-details?email=${response?.cognitoEmail}`)
      }
    } catch (error: any) {
      if (!isAuthorized(error)) {
        await handleLogOut()
        return
      }
      const errMessage = error?.response?.data?.error ?? error?.message ?? 'Oops, an unexpected error occured.'
      enqueueSnackbar(errMessage, { variant: 'error', autoHideDuration: 3000 })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Box mt={5} bgcolor={color.white}>
      <Box
        display="flex"
        p={3}
        borderBottom={`solid 1px ${color.secondary.dark[300]}`}
        flexDirection="column"
      >
        <Typography color={color.grey[900]} fontWeight={400} fontSize={24}>
          Customer Search
        </Typography>

        <Typography color={color.grey[600]} fontWeight={400} fontSize={16}>
          One field or more are required
        </Typography>

        <Box
          display="flex"
          alignItems={isMobile ? 'flex-start' : 'flex-end'}
          justifyContent="space-between"
          flexDirection={isMobile ? 'column' : 'row'}
          bgcolor={color.grey[50]}
          px={1.5}
          py={3}
          gap={2}
        >
          <Box width={isMobile ? '100%' : '38%'}>
            <FormLabel textcolor={color.grey[600]}>Account ID</FormLabel>

            <OutlinedInput
              name="accountId"
              placeholder="Enter Account ID"
              type="text"
              inputProps={{
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 12,
                minLength: 10
              }}
              fullWidth
              disabled={!isEmpty(email)}
              onChange={(e) => {
                setAccountId(e.target.value)
              }}
            />
          </Box>

          <Box width={isMobile ? '100%' : '38%'}>
            <FormLabel textcolor={color.grey[600]}>Email</FormLabel>

            <OutlinedInput
              placeholder="Enter Email"
              name="email"
              fullWidth
              disabled={!isEmpty(accountId)}
              onChange={(e) => {
                setEmail(e.target.value)
              }}
            />
          </Box>

          <Box
            display="flex"
            flexDirection="column"
            gap={2}
            width={isMobile ? '100%' : '20%'}
          >
            <MButton
              variant="contained"
              type="submit"
              size="large"
              rounded="true"
              fullWidth
              texttransform="none"
              sx={{ fontSize: 16 }}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={handleSubmit}
              loading={isLoading}
              disabled={isLoading || (isEmpty(accountId) && isEmpty(email))}
            >
              Search Customer
            </MButton>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
